<template>
	<v-app>

		<v-content>
			
			<!-- -------------------------------------------- -->
			<!--   Router for app -->
			<!-- -------------------------------------------- -->

			<router-view></router-view>
			

			<!-- -------------------------------------------- -->
			<!--   Progress overlay -->
			<!-- -------------------------------------------- -->

			<v-overlay
				
				:opacity="(waitingForAuth) ? 1 : 0"
				:value="showProgressWheel"
				:z-index="5"
				color="white"
				
			>
				<v-progress-circular
					:size="50"
					color="primary"
					indeterminate
				></v-progress-circular>
			</v-overlay>


			<!-- -------------------------------------------- -->
			<!--   Snackbar (which is kind of an alert) -->
			<!-- -------------------------------------------- -->

			<v-snackbar
				v-model="snackbar"
				:timeout="snackbarTimeout"
				top
				color="accent"
            >
				{{ snackbarText }}
				<v-btn
					color="white"
					text
					@click="snackbar = false"
				>
					Close
				</v-btn>
			</v-snackbar>


			<!-- -------------------------------------------- -->
			<!--   Confirm dialog -->
			<!-- -------------------------------------------- -->

			<Alert ref="alert"/>
			<Confirm ref="confirm"/>
			<SetName ref="setName"/>
			<ContentCreateNew ref="contentCreateNew"/>

		</v-content>

	</v-app>
</template>

<script>

// ----------------------------------------------------
//
//  Main script
//
// ----------------------------------------------------

import "quill/dist/quill.core.css"
import "quill/dist/quill.bubble.css"
import "quill/dist/quill.snow.css"
import '@/shift/agile.css';
import fb from '@/shift/fb';
import agile from '@/shift/agile';
import agileFb from '@/shift/agileFb';
import { AiEventBus } from '@/main';

// Components
import Alert from '@/components/attention/Alert.vue';
import Confirm from '@/components/attention/Confirm.vue';
import SetName from '@/components/SetName.vue';
import ContentCreateNew from '@/components/contenteditor/dialogs/ContentCreateNew.vue';


// ----------------------------------
//	VUE: Start default
// ----------------------------------

export default
{
	name: 'App',
	components: { Alert, Confirm, SetName, ContentCreateNew },


	// ----------------------------------
	//	VUE: Data and computed
	// ----------------------------------
	
	data: () => 
	({
		showProgressWheel: true,
		waitingForAuth: true,
		
		// A snackbar is a "mini-alert"
		snackbar: false,
		snackbarTimeout: 3000,
		snackbarText: '',
	}),

	computed:
    {
        routeKey()
        {
            return this.$route.params.key;
		},
	},



	// ----------------------------------------------------
    //
    //  VUE hooks: Create and Mount
    //
    // ----------------------------------------------------

	created()
	{
		// Make 'App' globally avaialable at $root and window. Todo: Not good? But no time to figure out the best way right now.
		// Prefix with "ai_" to remember that this is a custom Agile Interactive hack
		this.$root.ai_app = this;
		window.ai_app = this;
		this.$root.$agile = agile;
		agileFb;



		/* function resolveAfter2Seconds() {
			return new Promise(resolve => {
				setTimeout(() => {
				resolve('resolved');
				}, 10000);
			});
		}

		async function asyncCall() {
			console.log('calling');
			const result = await resolveAfter2Seconds();
			console.log(result);
			// expected output: 'resolved'
		}

		await asyncCall(); */


			
		// ----------------------------------
		//	FIREBASE:
		//	Check if we are authenticated!
		// ----------------------------------
		
		fb.auth.onAuthStateChanged(user =>
		{
			//console.log(user);

			// Get helper function to get abbreviation
			function getAvatarAbbreviation()
			{
				try {
					var ar = user.displayName.split(" ");
					return ar[0].charAt(0) + ((ar.length > 1) ? ar[ar.length - 1].charAt(0) : '');
				} catch (er)
				{
					return 'er';
				}
			}
			


			if (!user)
			{
				// ----------------------------------
				//	Not logged in, go to login
				// ----------------------------------

				this.waitingForAuth = false;
				this.showProgressWheel = false;

				this.$router.push('/login');
			}
			else
			{
				// ----------------------------------
				//	Logged in
				// ----------------------------------

				agile.loggedInUser.uid = user.uid;
				agile.loggedInUser.displayName = user.displayName;
				agile.loggedInUser.email = user.email;

				// Get username abbreviation for user icon in UI
				agile.loggedInUser.avatarAbbreviation = getAvatarAbbreviation();

				// Get token from custom claims to know what access the user got
				user.getIdTokenResult()
				.then((token) =>
				{

					this.waitingForAuth = false;
					this.showProgressWheel = false;

					if (token.claims && token.claims.accessCards)
					{
						agile.loggedInUser.accessCards = token.claims.accessCards;
					}
					else
					{
						agile.loggedInUser.accessCards = [];
					}
					
					agile.loggedInUser.keys = (token.claims.keys) ? token.claims.keys : [];

					//console.log(token);
					
					agile.loggedInUser.superAdmin = (Boolean(token.claims) && token.claims.superAdmin === true);

					//console.log(`* User ${agile.loggedInUser.displayName} logged in`);
					//console.log("* Super admin:", agile.loggedInUser.superAdmin);

					this.updateUserProjects();

					agileFb.getUserList();
					
				})
				.catch((er) =>
				{
					this.$root.$alert('Error', `Problem with idTokenResult >> ${er}`, { color: 'error' })
				});
			}


		});
	},

	mounted()
	{
		this.$root.$alert = this.$refs.alert.open;
		this.$root.$confirm = this.$refs.confirm.open;
		this.$root.$setName = this.$refs.setName.open;
		this.$root.$contentCreateNew = this.$refs.contentCreateNew.open;
		this.$root.$updateUserProjects = this.updateUserProjects;
	},



	// --------------------------------------------------------------------
    //
    //	VUE: Methods
    //
	// --------------------------------------------------------------------

	methods:
	{
		// -----------------------------------------
        //	Get projects for user
        // -----------------------------------------

        updateUserProjects()
        {
			// Create initial query (but not run it yet)
            let query = fb.db.collection('projects').where('disabled', '==', false);

			// -----------------------------------------
			// Get projects that a user is member of
			// -----------------------------------------

            const projectKeys = [];

			if (!agile.loggedInUser.superAdmin)
			{
				// Find the keys
				if (agile.loggedInUser.keys)
				{
					for (let i = 0; i < agile.loggedInUser.keys.length; i++)
					{
						projectKeys.push(agile.loggedInUser.keys[i].key);
					}
				}

				// Append keys to query // Todo: This will fail if we have more than 10 keys (max 10 items can be in firestore 'in' query)
				if (projectKeys.length > 0)
				{
					query = query.where('key', 'in', projectKeys);
				}
				else
				{
					// If no keys and not superadmin, then the user has no projects, and we abort!
					console.log("* App.vue - updateUserProjects() - User has no projects");
					return;
				}
				
			}


			// -----------------------------------------
			//	Ready to do the query!
			// -----------------------------------------

            query.get()
            .then((querySnapshot) =>
            {
                // Clear old project list for user
                agile.loggedInUser.projects = [];
				let hasAdminSomewhere = false;

                // Loop projects
                querySnapshot.forEach((doc) =>
                {
                    const docData = doc.data();
					let access;
					let clientAdmin = false; 	// Starting value, changed if true
					let groupAdmin = false; 	// Starting value, changed if true
					let projectAdmin = false; 	// Starting value, changed if true

					if (agile.loggedInUser.superAdmin)
					{
						clientAdmin = true;
						groupAdmin = true;
						projectAdmin = true;
						access = { dev:true, adm:true, edi:true, rea:true, gue:true };
					}
					else
					{
						for (let i = 0; i < agile.loggedInUser.keys.length; i++)
						{
							const key = agile.loggedInUser.keys[i].key;
							// Get levels of access
							if (docData.key === key)
							{
								access = agile.loggedInUser.keys[i].l;

								// Get which level potential admin access is at (client, group, project or none)
								for (let j = 0; j < agile.loggedInUser.accessCards.length; j++)
								{
									const ac = agile.loggedInUser.accessCards[j];
									
									if (key.substring(0,3) == ac.km && ac.key.charAt(4) == '*') // km means "Key Master" and "Master is the same level as client". Confusing I know...
									{
										clientAdmin = true;
										groupAdmin = true;
									}

									if (key.substring(0,3) == ac.km && key.substring(4,7) == ac.km && ac.key.charAt(4) == '*')
									{
										groupAdmin = true;
									}
									
								}

								break;
							}

						}
						projectAdmin = (access.dev || access.adm);
					}

					if (!hasAdminSomewhere && projectAdmin) hasAdminSomewhere = true;


					// ---------------------------------------------------------------------
					//	Make the project object (Should probably have been a class?)
					// ---------------------------------------------------------------------

                    if (docData)
                    {
                        agile.loggedInUser.projects.push( 
                        {
                            id: doc.id,
                            name: docData.name,
                            key: docData.key,
                            variable: docData.variable,
                            accessMap: docData.accessMap,
							clientName: docData.clientName,
							clientKey: docData.key.substring(0, 3),
							clientAdmin: clientAdmin,
							clientId: docData.clientId,
							groupName: docData.groupName,
							groupKey: docData.key.substring(4, 7),
							groupAdmin: groupAdmin,
							groupId: docData.groupId,
							projectName: docData.name,
							projectKey: docData.key.substring(8, 11),
							projectAdmin: projectAdmin,
							prettyName: docData.prettyName,
							access: access,
                        });
                    }
                    else
                    {
                        console.log('* App.vue - updateUserProjects() - No doc data()?');
                    }
				});
				
                agile.sortBy(agile.loggedInUser.projects, 'clientName');

				// If the user has admin rights in any of the project he is a member of (typically used to filter UI)
				agile.loggedInUser.adminInSomeProject = hasAdminSomewhere;

                // Update current project if route is available
				if (this.routeKey != '')
				{
					agile.setCurrentProjectByKey(this.routeKey);
					agile.loggedInUser.currentProject == agile.getProjectByKey(this.routeKey);
				}
				

				// Aaaaaand we are done! Emit event that we are done!
				AiEventBus.$emit('authenticationComplete', true);
            })
            .catch((error) =>
            {
                console.log("Error", error);
			});
			




        },
	},

};
</script>
